// Sino de notificações - lembretes do Motor de Médias
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Bell, ArrowRight, Check, Loader2 } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

export default function NotificacoesBell({ userId, theme = 'light' }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [lembretes, setLembretes] = useState([]);
  const [pendentes, setPendentes] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await api.get(`/grade-engine/lembretes/me?destinatario_id=${userId}&status=pendente`);
      setLembretes(res.data?.lembretes || []);
      setPendentes(res.data?.pendentes || 0);
    } catch (_) { /* silencioso */ }
  }, [userId]);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000); // refresh a cada 60s
    return () => clearInterval(t);
  }, [load]);

  const marcarLido = async (id) => {
    setLoading(true);
    try {
      await api.post(`/grade-engine/lembretes/${id}/marcar-lido`);
      await load();
    } catch (_) {
      toast.error('Erro ao marcar como lido');
    } finally {
      setLoading(false);
    }
  };

  const irLancar = async (l) => {
    if (l.id) await marcarLido(l.id);
    const params = new URLSearchParams();
    if (l.class_group_id) params.append('class_group_id', l.class_group_id);
    if (l.subject_id) params.append('subject_id', l.subject_id);
    if (l.trimestre) params.append('trimestre', l.trimestre);
    setOpen(false);
    navigate(`/teacher-portal?${params}`);
  };

  const isDark = theme === 'dark';
  const btnCls = isDark
    ? 'text-white hover:bg-white/20'
    : 'text-slate-700 hover:bg-slate-100';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={`relative ${btnCls} px-2`}
          data-testid="notificacoes-sino"
        >
          <Bell className="w-5 h-5" />
          {pendentes > 0 && (
            <span
              className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 ring-2 ring-purple-700"
              data-testid="notificacoes-badge"
            >
              {pendentes > 9 ? '9+' : pendentes}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[360px] p-0 bg-white border-slate-200 shadow-xl"
        align="end"
        data-testid="notificacoes-popover"
      >
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <div>
            <p className="font-semibold text-slate-800">Notificações</p>
            <p className="text-xs text-slate-500">{pendentes} pendência(s) aguardando você</p>
          </div>
          {pendentes > 0 && <Badge className="bg-red-100 text-red-700">{pendentes}</Badge>}
        </div>

        <div className="max-h-[400px] overflow-y-auto">
          {lembretes.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <Check className="w-8 h-8 mx-auto mb-2 text-emerald-400" />
              <p className="text-sm">Tudo em dia! Sem notificações pendentes.</p>
            </div>
          ) : (
            lembretes.map((l) => (
              <div
                key={l.id}
                className="px-4 py-3 border-b border-slate-50 hover:bg-slate-50"
                data-testid={`lembrete-${l.id}`}
              >
                <div className="flex items-start gap-2 mb-2">
                  <div
                    className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0"
                    style={{
                      backgroundColor: l.prioridade === 'urgente' ? '#EF4444' : '#F59E0B'
                    }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-800 leading-snug">{l.mensagem}</p>
                    {l.faltantes?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {l.faltantes.map((f) => (
                          <Badge key={f} className="bg-red-50 text-red-700 text-[10px] border border-red-200">
                            {f}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => irLancar(l)}
                    disabled={loading}
                    className="bg-purple-600 hover:bg-purple-700 text-white h-7 text-xs flex-1"
                    data-testid={`lembrete-ir-${l.id}`}
                  >
                    <ArrowRight className="w-3 h-3 mr-1" />
                    Ir Lançar
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => marcarLido(l.id)}
                    disabled={loading}
                    className="h-7 text-xs"
                    data-testid={`lembrete-lido-${l.id}`}
                  >
                    {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
